// Localização: frontend/src/components/AgendaTarefas/AgendaTarefaFormRecorrente.jsx (versão 0.1.8)

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Calendar, Clock, Repeat, XCircle, Save, Activity, Droplets, RotateCcw, UtensilsCrossed, Dumbbell, Pill, FileText, User, ChevronDown, Scale, Droplet } from "lucide-react";

// Hooks
import { usePacienteList } from "../../hooks/usePacienteList";
import { useAgendaTarefas } from "../../hooks/useAgendaTarefas"; 

// Componentes
import Modal from "../UI/Modal"; 
import DropdownMenu from "../UI/DropdownMenu";
// 🛑 REMOVEMOS A IMPORTAÇÃO: O ConfirmacaoModal será gerenciado pelo componente pai
// import ConfirmacaoModal from "../Common/ConfirmacaoModal";
import Alert from "../UI/Alert";
import { getPacienteFotoUrl } from "../../utils/imageUtils";

// Mapeamento dos tipos de tarefa para o backend com ícones do Lucide
const TIPO_TAREFAS = [
    { value: "Sinais Vitais", label: "Sinais Vitais", icon: Activity },
    { value: "Higienização", label: "Higienização", icon: Droplets },
    { value: "Mudança de Decúbito", label: "Mudança de Decúbito", icon: RotateCcw },
    { value: "Alimentação", label: "Alimentação", icon: UtensilsCrossed },
    { value: "Atividade Física", label: "Atividade Física", icon: Dumbbell },
    { value: "Medicação", label: "Medicação", icon: Pill },
    { value: "Pesagem", label: "Pesagem", icon: Scale },
    { value: "Glicemia", label: "Glicemia", icon: Droplet },
    { value: "Outro", label: "Outro", icon: FileText },
];

// Funções auxiliares de formatação (inalteradas)
const formatToDateLocal = (isoString) => {
    if (!isoString) return "";
    const datePart = isoString.split('T')[0];
    return datePart;
};
const formatToTimeLocal = (isoString) => {
    if (!isoString) return "";
    const timePart = isoString.split('T')[1]?.slice(0, 5) || isoString.slice(0, 5); 
    return timePart;
};


/**
 * Formulário para criação e edição de regras de tarefas RECORRENTES.
 * @param {boolean} isOpen - Controla a visibilidade
 * @param {function} onClose - Função para fechar o modal
 * @param {function} onSucesso - Callback de sucesso
 * @param {object | null} tarefaParaEditar - Dados da regra para edição
 * @param {function} onOpenConfirmacao - NOVO PROP: Função para solicitar que o pai abra o modal de confirmação.
 */
const AgendaTarefaFormRecorrente = ({ 
    isOpen, 
    onClose, 
    onSucesso, 
    tarefaParaEditar, 
    onOpenConfirmacao 
}) => {
    const isEditing = !!tarefaParaEditar;
    
    const { pacientes, isLoading: isLoadingPacientes } = usePacienteList();
    const { createTarefaRecorrente, updateTarefaRecorrente } = useAgendaTarefas(); 

    // Usando useMemo para garantir que initialFormData só seja recriado se for realmente necessário
    const initialFormData = useMemo(() => ({
        paciente_id: "",
        tipo_tarefa: TIPO_TAREFAS[0].value, // Padrão: Sinais Vitais
        descricao: "",
        data_inicio: "", 
        horario_base: "", 
        data_fim: "",
        permanente: false,
        recorrente: true, 
        frequencia_intervalo_horas: 4, 
        frequencia_vezes_ao_dia: 6, 
        instrucoes_especificas: "", 
    }), []);

    const [formData, setFormData] = useState(initialFormData);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alerta, setAlerta] = useState(null);
    // 🛑 REMOVEMOS O ESTADO: A confirmação é gerenciada externamente
    // const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    // Ref para rastrear qual tarefa já foi carregada, evitando reset durante edição
    const tarefaCarregadaRef = useRef(null);

    // Efeito para carregar dados da tarefa no modo de edição/limpar na criação
    useEffect(() => {
        // Se está editando e a tarefa mudou (novo ID), recarrega os dados
        const tarefaId = tarefaParaEditar?.id || tarefaParaEditar?.regra_id || tarefaParaEditar?.regra_recorrente?.id;
        
        if (isEditing && tarefaParaEditar && tarefaCarregadaRef.current !== tarefaId) {
            tarefaCarregadaRef.current = tarefaId;
            // Para tarefas recorrentes, os dados da regra estão em regra_recorrente
            const regra = tarefaParaEditar.regra_recorrente || {};
            
            // Formata horario_base: pode vir como "HH:MM:SS" ou "HH:MM" do banco
            let horarioBaseFormatado = "";
            if (regra.horario_base) {
                // Se já está no formato de hora (HH:MM:SS ou HH:MM), pega apenas HH:MM
                // Remove segundos se existirem e garante formato HH:MM
                const horaLimpa = regra.horario_base.trim();
                if (horaLimpa.includes(':')) {
                    const partes = horaLimpa.split(':');
                    if (partes.length >= 2) {
                        // Garante formato HH:MM com zeros à esquerda
                        const horas = partes[0].padStart(2, '0');
                        const minutos = partes[1].padStart(2, '0');
                        horarioBaseFormatado = `${horas}:${minutos}`;
                    } else {
                        horarioBaseFormatado = horaLimpa.slice(0, 5);
                    }
                } else {
                    horarioBaseFormatado = horaLimpa.slice(0, 5);
                }
            } else if (tarefaParaEditar.hora_agendada) {
                // Fallback: tenta extrair da hora_agendada (formato ISO)
                horarioBaseFormatado = formatToTimeLocal(tarefaParaEditar.hora_agendada);
            }
            
            // Formata data_fim: pode vir como "YYYY-MM-DD" do banco ou como ISO string
            let dataFimFormatada = "";
            if (regra.data_fim) {
                // Se já está no formato YYYY-MM-DD, usa diretamente; senão, extrai da ISO string
                dataFimFormatada = regra.data_fim.includes('T') 
                    ? formatToDateLocal(regra.data_fim) 
                    : regra.data_fim;
            }
            
            setFormData({
                paciente_id: tarefaParaEditar.paciente_id || "",
                tipo_tarefa: regra.tipo_tarefa_regra || tarefaParaEditar.tipo_tarefa || TIPO_TAREFAS[0].value,
                descricao: regra.descricao_regra || tarefaParaEditar.descricao || "",
                data_inicio: regra.data_inicio || formatToDateLocal(tarefaParaEditar.hora_agendada) || "",
                horario_base: horarioBaseFormatado, 
                data_fim: dataFimFormatada,
                permanente: !regra.data_fim, 
                recorrente: true, 
                frequencia_intervalo_horas: regra.frequencia_intervalo_horas || 4, 
                frequencia_vezes_ao_dia: regra.frequencia_vezes_ao_dia || 6, 
                instrucoes_especificas: regra.instrucoes_especificas || regra.descricao_regra || tarefaParaEditar.descricao || "",
            });
        } else {
            // Reset apenas quando não está editando
            tarefaCarregadaRef.current = null;
            setFormData(initialFormData);
        }
        // Limpa alertas ao abrir/mudar o modo
        setAlerta(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, tarefaParaEditar?.id, tarefaParaEditar?.regra_id, tarefaParaEditar?.regra_recorrente?.id]); // Usa apenas IDs para evitar reset durante edição


    // Função para fechar o modal
    const handleCancel = () => {
        onClose(); 
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleDateTimeChange = (e) => {
        const { value } = e.target;
        console.log('[AgendaTarefaFormRecorrente] handleDateTimeChange - valor recebido:', value);
        const [datePart, timePart] = value.split('T');
        console.log('[AgendaTarefaFormRecorrente] handleDateTimeChange - datePart:', datePart, 'timePart:', timePart);
        setFormData((prev) => {
            const newData = {
                ...prev,
                data_inicio: datePart || '',
                horario_base: timePart || '',
            };
            console.log('[AgendaTarefaFormRecorrente] handleDateTimeChange - novo formData:', newData);
            return newData;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.data_inicio || !formData.horario_base || !formData.paciente_id) {
            setAlerta({ tipo: "error", mensagem: "Paciente, Data e Hora Inicial são obrigatórias." });
            return;
        }
        if (!formData.permanente && !formData.data_fim) {
            setAlerta({ tipo: "error", mensagem: "Se a tarefa não é permanente, a Data Fim é obrigatória." });
            return;
        }

        setAlerta(null);
        
        // 🛑 NOVO FLUXO: Envia os dados necessários e a função de envio para o componente pai.
        if (onOpenConfirmacao) {
            // Garantir que paciente_id seja convertido para número se necessário
            const pacienteIdFormatado = formData.paciente_id ? Number(formData.paciente_id) || formData.paciente_id : null;
            
            console.log('[AgendaTarefaFormRecorrente] Enviando dados para confirmação:', {
                paciente_id: formData.paciente_id,
                paciente_id_formatado: pacienteIdFormatado,
                tipo_original: typeof formData.paciente_id,
                data_inicio: formData.data_inicio,
                horario_base: formData.horario_base
            });
            
            onOpenConfirmacao({
                isEditing,
                dataInicio: formData.data_inicio,
                horarioBase: formData.horario_base,
                paciente_id: pacienteIdFormatado,
                onConfirm: handleConfirmarEnvio,
            });
        } else {
            // Caso o componente pai não passe a prop de confirmação, envia direto
            handleConfirmarEnvio();
        }
    };

    const handleConfirmarEnvio = async () => {
        // 🛑 REMOVEMOS: setShowConfirmModal(false) - pois o modal é externo.
        
        setIsSubmitting(true);
        setAlerta(null);

        try {
            const payload = {
                paciente_id: formData.paciente_id,
                tipo_tarefa: formData.tipo_tarefa, 
                descricao: formData.descricao || null, 
                
                data_inicio: formData.data_inicio,
                horario_base: formData.horario_base + ':00', 
                data_fim: formData.permanente ? null : formData.data_fim || null, 

                frequencia_intervalo_horas: Number(formData.frequencia_intervalo_horas),
                frequencia_vezes_ao_dia: Number(formData.frequencia_vezes_ao_dia), 

                instrucoes_especificas: formData.descricao, 
            };

            let response;
            if (isEditing) {
                // Para tarefas recorrentes, usar o ID da regra, não o ID da tarefa
                const regraId = tarefaParaEditar.regra_id || tarefaParaEditar.regra_recorrente?.id;
                if (!regraId) {
                    throw new Error('ID da regra de recorrência não encontrado. Não é possível atualizar.');
                }
                response = await updateTarefaRecorrente(regraId, payload);
            } else {
                response = await createTarefaRecorrente(payload);
            }

            setAlerta({ 
                tipo: "success", 
                mensagem: response.message || (isEditing ? "Regra de Agendamento atualizada com sucesso!" : "Regra e primeira tarefa criadas com sucesso!") 
            });

            if (onSucesso) onSucesso();
            
        } catch (error) {
            console.error(isEditing ? "Erro ao editar regra de tarefa:" : "Erro ao criar tarefa recorrente:", error);
            setAlerta({
                tipo: "error",
                mensagem: error.message || error.response?.data?.message || `Falha ao ${isEditing ? 'atualizar' : 'criar'} a regra de agendamento.`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Calcula o valor do input datetime-local a partir do formData
    // Garante que horario_base está no formato HH:MM (sem segundos)
    const horarioBaseFormatado = formData.horario_base 
        ? (() => {
            const partes = formData.horario_base.split(':');
            if (partes.length >= 2) {
                // Garante formato HH:MM com zeros à esquerda
                const horas = partes[0].padStart(2, '0');
                const minutos = partes[1].padStart(2, '0');
                return `${horas}:${minutos}`;
            }
            return formData.horario_base.slice(0, 5);
        })()
        : '';
    
    const initialDateTime = formData.data_inicio && horarioBaseFormatado
                            ? `${formData.data_inicio}T${horarioBaseFormatado}` 
                            : '';
    
    // Debug: log do valor do datetime-local
    useEffect(() => {
        if (isEditing) {
            console.log('[AgendaTarefaFormRecorrente] Debug - formData:', formData);
            console.log('[AgendaTarefaFormRecorrente] Debug - horarioBaseFormatado:', horarioBaseFormatado);
            console.log('[AgendaTarefaFormRecorrente] Debug - initialDateTime:', initialDateTime);
        }
    }, [formData, horarioBaseFormatado, initialDateTime, isEditing]);

    const modalTitle = isEditing ? `✏️ Editar Regra Recorrente` : "✨ Criar Novo Agendamento Recorrente";

    return (
        <Modal
            isOpen={isOpen} 
            onClose={handleCancel} 
            title={modalTitle} 
            size="lg" 
        >
            {alerta && <div className="mb-4"><Alert type={alerta.tipo} message={alerta.mensagem} /></div>}

            <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Paciente e Tipo de Tarefa */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <User size={16} className="text-gray-500" />
                            Paciente
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    disabled={isEditing}
                                    className={`w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition ${
                                        isEditing ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const pacienteSelecionado = pacientes?.find(p => String(p.id) === String(formData.paciente_id));
                                            const fotoUrl = pacienteSelecionado?.fotoURL ? getPacienteFotoUrl(pacienteSelecionado.fotoURL) : null;
                                            return (
                                                <>
                                                    <div className="relative h-6 w-6 flex-shrink-0">
                                                        {fotoUrl ? (
                                                            <img 
                                                                src={fotoUrl} 
                                                                alt={pacienteSelecionado?.nome_completo || 'Paciente'} 
                                                                className="h-6 w-6 rounded-full object-cover border border-gray-200"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    if (e.target.nextSibling) {
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                    }
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div className={`h-6 w-6 rounded-full flex items-center justify-center ${fotoUrl ? 'hidden' : 'flex'}`}>
                                                            <User size={16} className="text-gray-500" />
                                                        </div>
                                                    </div>
                                                    <span className="text-gray-700">
                                                        {pacienteSelecionado?.nome_completo || (isLoadingPacientes ? 'Carregando...' : 'Selecione o paciente')}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={isLoadingPacientes ? 
                                [{ id: 'loading', label: 'Carregando...', disabled: true }] :
                                pacientes?.map((p) => ({
                                    id: String(p.id),
                                    label: p.nome_completo,
                                    image: p.fotoURL ? getPacienteFotoUrl(p.fotoURL) : null,
                                    imageAlt: p.nome_completo,
                                    icon: User,
                                    iconColor: 'text-gray-600',
                                    onClick: () => {
                                        if (!isEditing) {
                                            setFormData(prev => ({
                                                ...prev,
                                                paciente_id: String(p.id)
                                            }));
                                        }
                                    }
                                })) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Activity size={16} className="text-gray-500" />
                            Tipo de Tarefa
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition"
                                >
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const tipoSelecionado = TIPO_TAREFAS.find(t => t.value === formData.tipo_tarefa);
                                            const Icon = tipoSelecionado?.icon || Activity;
                                            return (
                                                <>
                                                    <Icon size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {tipoSelecionado?.label || "Selecione o tipo de tarefa"}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={TIPO_TAREFAS.map(t => ({
                                id: t.value,
                                label: t.label,
                                icon: t.icon,
                                iconColor: 'text-gray-600',
                                onClick: () => {
                                    setFormData(prev => ({
                                        ...prev,
                                        tipo_tarefa: t.value
                                    }));
                                }
                            }))}
                            position="bottom-left"
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Horário inicial (Data e Hora) */}
                <div>
                    <label className="text-gray-600 font-medium mb-1 flex items-center gap-1">
                        <Clock size={16} /> Data e Hora Base (Início do Ciclo)
                    </label>
                    <input
                        type="datetime-local"
                        value={initialDateTime} 
                        onChange={handleDateTimeChange}
                        required
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                    />
                </div>

                {/* Descrição */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1">
                        Descrição / Instruções
                    </label>
                    <textarea
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Descreva a tarefa ou instruções específicas..."
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                        required
                    ></textarea>
                </div>

                {/* Opções de frequência */}
                <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
                    <p className="text-lg font-semibold text-gray-700 flex items-center gap-2"><Repeat size={20} /> Frequência</p>
                    
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-emerald-50 p-4 rounded-md border border-emerald-100"
                    >
                        <div>
                            <label className="block text-gray-600 mb-1">Intervalo (Horas)</label>
                            <input
                                type="number"
                                name="frequencia_intervalo_horas"
                                min="1"
                                max="168"
                                value={formData.frequencia_intervalo_horas}
                                onChange={handleChange}
                                required
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-600 mb-1">
                                Vezes ao Dia (Estimativa)
                            </label>
                            <input
                                type="number"
                                name="frequencia_vezes_ao_dia"
                                min="1"
                                max="24"
                                value={formData.frequencia_vezes_ao_dia}
                                onChange={handleChange}
                                required
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                            />
                        </div>
                    </div>
                </div>

                {/* Data Fim / Permanente */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-gray-200 pt-3 gap-3">
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="permanente"
                            name="permanente"
                            checked={formData.permanente}
                            onChange={handleChange}
                            className="h-4 w-4 text-indigo-500 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="permanente" className="text-gray-600 font-medium">
                            Agendamento Permanente (Sem data final)
                        </label>
                    </div>

                    {!formData.permanente && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                            <label className="text-gray-600 font-medium whitespace-nowrap">Data Final:</label>
                            <Calendar size={16} className="text-gray-500 hidden sm:block" />
                            <input
                                type="date"
                                name="data_fim"
                                value={formData.data_fim}
                                onChange={handleChange}
                                className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                                required
                            />
                        </div>
                    )}
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={handleCancel} 
                        className="flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition"
                        disabled={isSubmitting}
                    >
                        <XCircle size={18} /> Cancelar
                    </button>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`flex items-center gap-2 font-semibold py-2 px-4 rounded-lg shadow-md transition disabled:opacity-50 ${isEditing ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                    >
                        {isSubmitting 
                            ? (isEditing ? "Salvando Edição..." : "Criando Regra...") 
                            : (isEditing ? <> <Save size={18} /> Salvar Regra</> : <> <Repeat size={18} /> Criar Agendamento</>)}
                    </button>
                </div>
            </form>

            {/* 🛑 O ConfirmacaoModal FOI REMOVIDO DAQUI. O componente pai deve renderizá-lo. */}
        </Modal>
    );
};

export default AgendaTarefaFormRecorrente;